import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button } from "semantic-ui-react";
import { editModeOn, selectEditMode } from "../../slices/editMode/editModeSlice";
import { selectCurrentUserApiToken, selectCurrentUserId, selectSseConnectionId } from "../../slices/currentUser/currentUserSlice";
import { selectGlobalLoading } from "../../slices/globalLoading/globalLoadingSlice";
import { selectAllResources, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import { selectViewingOlderVersion } from "../../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { selectChangedResourcesList } from "../../slices/changedResourcesList/changedResourcesListSlice";
import { selectTabFocus } from "../../slices/tabPane/tabPaneSlice";
import sortResourceElements from "../resourceDisplayCommonFunctions/sortResourceElements";
import createBundleJson from "../resourceDisplayCommonFunctions/createBundleJson";
import submitUpdatedResource from "../resourceDisplayCommonFunctions/submitUpdatedResource";
import PublishButton from "./PublishButton";
import server_config from "../../ServerConfiguration";

const fevir_node_server_address = server_config["fevir_node_server_address"];

const handleDownload = (event, jsonString, resourceId) => {
    event.preventDefault();
    const blob = new Blob([jsonString], { type: 'text/json' });
    const a = document.createElement('a');
    a.download = 'FOI' + resourceId + '.json';
    a.href = window.URL.createObjectURL(blob);
    const clickEvt = new MouseEvent('click', { view: window, bubbles: true, cancelable: true });
    a.dispatchEvent(clickEvt);
    a.remove();
};

const handleBundleDownload = (event, fhirEntryString, resourceId, resourceCollection) => {
    event.preventDefault();
    let bundleJson = createBundleJson(JSON.parse(fhirEntryString), resourceId, resourceCollection);
    if (bundleJson) {
        let jsonString = JSON.stringify(bundleJson);
        const blob = new Blob([jsonString], { type: 'text/json' });
        const a = document.createElement('a');
        a.download = 'BundleForFOI' + resourceId + '.json';
        a.href = window.URL.createObjectURL(blob);
        const clickEvt = new MouseEvent('click', { view: window, bubbles: true, cancelable: true });
        a.dispatchEvent(clickEvt);
        a.remove();
    }
};

const JSONViewResourceButtons = ({ foi, jsonViewEditingState, setJsonViewEditingState, setParentResourceAlertModalState, statusChanged = false }) => {
    const { addToast } = useToasts();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const editMode = useSelector(state => selectEditMode(state));
    const userId = useSelector(selectCurrentUserId);
    const apiToken = useSelector(selectCurrentUserApiToken);
    const connectionId = useSelector(selectSseConnectionId);
    const siteIsLoading = useSelector(selectGlobalLoading);
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson || parentResourceDetail?.resourceJson;
    const pubStatus = parentResourceDetail?.usageData?.status;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const changedResourcesList = useSelector(state => selectChangedResourcesList(state));
    const tabFocus = useSelector(state => selectTabFocus(state));
    const editPermission = parentResourceDetail?.usageData?.editpermission;
    const readPermission = parentResourceDetail?.usageData?.readpermission;

    return <div>
        {resourceJson && <>{
            editMode
                ?
                <>
                    {userId ?
                        <>
                            <>
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Sort Element Order"
                                    onClick={() => {
                                        sortResourceElements(dispatch, jsonViewEditingState,
                                            setJsonViewEditingState, apiToken);
                                    }}
                                    disabled={siteIsLoading} />
                            </>
                            &nbsp;&nbsp;&nbsp;
                            {/**TODO -- rewrite the 'JSON Tree Writer' interface within the Redux code as part of the JSON View UI */}
                            {(pubStatus === "active") &&
                                <>
                                    <Button className="formButton" style={{ color: "#000000" }}
                                        content="Edit Resource in JSON Tree Writer"
                                        onClick={() => {
                                            window.open("https://khalid-shahin.github.io/JSON-Tree-Writer/?server=" +
                                                encodeURIComponent(fevir_node_server_address) +
                                                "&extrarequestheader=functionid&extrarequestinfo=getfhirresource&responseobjectname=fhirjsonstring&string=true&resourcetype=" +
                                                resourceJson.resourceType +
                                                "&resourceid=" +
                                                resourceJson.id, '_blank');
                                        }}
                                        disabled={viewingOlderVersion} />
                                    &nbsp;&nbsp;&nbsp;
                                </>
                            }
                            <Button className="formButton" style={{ color: "#000000" }}
                                content="Submit Updated Resource"
                                onClick={() => {
                                    submitUpdatedResource(foi, parentResourceDetail, apiToken, connectionId,
                                        dispatch, resourceCollection,
                                        changedResourcesList, tabFocus, jsonViewEditingState,
                                        setJsonViewEditingState, setParentResourceAlertModalState, addToast);
                                }}
                                disabled={(!jsonViewEditingState.fhirEntryStringChange && !statusChanged) || viewingOlderVersion}
                            />
                        </>
                        :
                        <>&nbsp;&nbsp;&nbsp;Please log back in.</>
                    }
                </>
                :
                <>
                    {(userId && editPermission) &&
                        <>
                            &nbsp;&nbsp;&nbsp;
                            <Button className="formButton" style={{ color: "#000000" }}
                                content="Edit Resource"
                                onClick={() => { dispatch(editModeOn()); }}
                                disabled={viewingOlderVersion} />
                            {jsonViewEditingState.status === "draft" &&
                                <>
                                    &nbsp;&nbsp;&nbsp;
                                    <PublishButton foi={foi}
                                        jsonViewEditingState={jsonViewEditingState}
                                        setJsonViewEditingState={setJsonViewEditingState} />
                                </>
                            }
                        </>}
                    {(resourceJson.resourceType && (readPermission || editPermission)) &&
                        <>
                            &nbsp;&nbsp;&nbsp;
                            <Button className="formButton" style={{ color: "#000000" }}
                                content="Clone Resource"
                                onClick={() => {
                                    navigate("/create/" + resourceJson.resourceType + "/" + resourceJson.id);
                                }}
                                disabled={viewingOlderVersion} />
                        </>
                    }
                    &nbsp;&nbsp;&nbsp;
                    <Button className="formButton" style={{ color: "#000000" }}
                        content="Add New Resource"
                        onClick={() => { navigate("/create"); }} />
                    &nbsp;&nbsp;&nbsp;
                    {(resourceJson.resourceType && (readPermission || editPermission)) &&
                        <>
                            <Button className="formButton" style={{ color: "#000000" }}
                                content="Download JSON"
                                onClick={event => handleDownload(event, jsonViewEditingState.fhirEntryString || '', foi)} />
                            &nbsp;&nbsp;&nbsp;
                            <Button className="formButton" style={{ color: "#000000" }}
                                content="Download Bundle"
                                onClick={event => handleBundleDownload(event,
                                    jsonViewEditingState.fhirEntryString || '', foi, resourceCollection)} />
                        </>
                    }
                </>
        }</>}
    </div>
}

export default JSONViewResourceButtons;